import { Statuswahl, type StatusTon, type StatusZeichen } from "./Statuswahl";

/* Wertwahl (BAUPLAN-FEINSCHLIFF.md, Abschnitt 3.2 bis 3.4): dünne Hülle um Statuswahl für Werte, die sofort gelten, zum Beispiel Rolle,
   Priorität, Wo, Sichtbarkeit oder Filter. Dieselbe Pille und dasselbe Fenster, aber weiß und ohne Zeichen, solange eine Option nichts
   anderes sagt. Mit `filter` steht ohne Wahl der Name in einer weißen Pille („Ressort“), mit Wahl der Wert in einer schwarzen
   („Technik“). Der Name für Screenreader beginnt immer mit „Name: Wert“. */

export interface WertOption<T extends string> {
  wert: T;
  text: string;
  erklaerung?: string | undefined;
  /** Vorgabe weiß. Priorität Hoch nimmt `hoch`. */
  ton?: StatusTon | undefined;
  zeichen?: StatusZeichen | undefined;
  /** Farbpunkt vor dem Wort (Vorhaben) */
  punkt?: string | undefined;
}

interface Props<T extends string> {
  /** „Rolle“, „Priorität“, „Ressort“: Kopf im Fenster, Anfang des Namens für Screenreader und Wort der leeren Filterpille */
  art: string;
  /** Wessen Wert, für „Rolle von Lena Brandt“. Ohne: nur `art`. */
  name?: string | undefined;
  optionen: readonly WertOption<T>[];
  wert: T;
  /** Ohne Recht: gestrichelt, ohne Pfeil, nicht klickbar */
  darf?: boolean | undefined;
  laeuft?: boolean | undefined;
  /** Feste Breite in px in Spalten einer Liste (Rolle 150) */
  breite?: number | undefined;
  /** Für Tabellenzellen, Schrift 13 px wie die Statuspille in Aufgabenlisten */
  klein?: boolean | undefined;
  /** Füllt die Zelle statt fester Breite: Wort in einer Zeile, sonst „…“, der volle Wert im title (Aufgabenliste) */
  fuellen?: boolean | undefined;
  /** Leises Wort vor dem Wert in der Pille, zum Beispiel „Gruppieren:“ */
  vorsatz?: string | undefined;
  /** Filter: dieser Wert heißt „nicht gefiltert“ (weiß mit `art`), jeder andere schwarz mit dem Wert */
  filter?: { leer: T } | undefined;
  onWechsel: (neu: T, alt: T) => void;
}

export function Wertwahl<T extends string>({ art, name, optionen, wert, darf = true, laeuft, breite, klein, fuellen, filter, vorsatz, onWechsel }: Props<T>) {
  const liste = optionen.map((o) => ({
    wert: o.wert,
    text: o.text,
    erklaerung: o.erklaerung ?? "",
    ton: o.ton ?? ("weiss" as const),
    zeichen: o.zeichen,
    punkt: o.punkt,
  }));
  const gewaehlt = optionen.find((o) => o.wert === wert);
  const anzeige = filter
    ? wert === filter.leer
      ? { text: art, ton: "weiss" as const }
      : { text: gewaehlt?.text ?? wert, ton: "stark" as const }
    : undefined;
  return (
    <Statuswahl
      art={art}
      name={name ?? ""}
      optionen={liste}
      wert={wert}
      darf={darf}
      laeuft={laeuft}
      breite={breite}
      klein={klein}
      fuellen={fuellen}
      anzeige={anzeige}
      vorsatz={vorsatz}
      onWechsel={onWechsel}
    />
  );
}
